import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Download,
  Folder,
  Loader2,
  ShieldCheck,
  LockKeyhole,
  ArrowLeft,
  Building2,
  UserPlus,
  Users2,
  Trash2,
  Plus,
  Circle,
  CheckCircle2,
  ClipboardList,
  Pencil,
  X,
  Check,
  Upload,
  Tag,
} from "lucide-react";
import { toast } from "sonner";
import client, { API } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { useMemberSections } from "@/components/dashboard/memberNav";

function fmtSize(bytes = 0) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch (_) {
    return iso;
  }
}

function fmtDueDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString();
  } catch (_) {
    return iso;
  }
}

const ROLE_TONES = {
  owner: "bg-primary/10 text-primary border-primary/20",
  editor: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  viewer: "bg-muted text-muted-foreground border-border",
};

const PRIORITY_TONES = {
  low: "bg-muted text-muted-foreground border-border",
  medium: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  high: "bg-rose-500/10 text-rose-600 border-rose-500/20",
};

/**
 * Authenticated member view for a single data room. Renders the linked
 * Company, Documents, room-scoped Tasks (with assignee), and Invited
 * Members. Owner/admin affordances (invite members, add / delete tasks,
 * remove members) are gated on `my_role === 'owner'` from the API.
 */
export default function MemberDataRoom() {
  const { roomId } = useParams();
  const { t } = useTranslation();
  const { user, loading: authLoading, loginWithGoogle } = useAuth();
  const { unreadCount } = useNotifications();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: null, room: null, docs: [] });
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);

  const loadRoom = useCallback(async () => {
    try {
      const res = await client.get(`/data-rooms/${roomId}`);
      setState({ loading: false, error: null, room: res.data.room, docs: res.data.documents || [] });
    } catch (e) {
      const code = e?.response?.status;
      setState({
        loading: false,
        error: code === 404 ? "not_found_or_forbidden" : "generic",
        room: null,
        docs: [],
      });
    }
  }, [roomId]);

  const loadTasks = useCallback(async () => {
    setTasksLoading(true);
    try {
      const res = await client.get(`/data-rooms/${roomId}/tasks`);
      setTasks(res.data?.items || []);
    } catch (_) {
      setTasks([]);
    } finally {
      setTasksLoading(false);
    }
  }, [roomId]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setState({ loading: false, error: "auth", room: null, docs: [] });
      return;
    }
    loadRoom();
  }, [user, authLoading, loadRoom]);

  useEffect(() => {
    if (state.room) loadTasks();
  }, [state.room, loadTasks]);

  const [uploading, setUploading] = useState(false);
  const [pendingTags, setPendingTags] = useState("");
  const [tagDraft, setTagDraft] = useState({ doc_id: null, value: "" });

  const sections = useMemberSections({ role: user?.role, unreadCount: unreadCount || 0 });
  const goToTab = useCallback((id) => navigate(`/dashboard?tab=${id}`), [navigate]);

  const uploadDoc = async (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    setUploading(true);
    const body = new FormData();
    body.append("file", file);
    body.append("tags", pendingTags);
    try {
      await client.post(`/data-rooms/${roomId}/documents`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("dataRoom.uploadDone", { defaultValue: "Document uploaded" }));
      setPendingTags("");
      await loadRoom();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setUploading(false);
    }
  };

  const saveTags = async (doc_id, value) => {
    try {
      await client.patch(`/data-rooms/${roomId}/documents/${doc_id}`, { tags: value });
      setTagDraft({ doc_id: null, value: "" });
      await loadRoom();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  const download = (doc_id, name) => {
    const a = document.createElement("a");
    a.href = `${API}/data-rooms/${roomId}/documents/${doc_id}/download`;
    a.download = name || "document";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  if (state.loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (state.error === "auth") {
    return (
      <div className="mx-auto max-w-lg px-4 py-24 text-center" data-testid="member-data-room-auth">
        <LockKeyhole className="h-8 w-8 text-primary mx-auto mb-4" />
        <h1 className="font-display text-2xl font-semibold">{t("dataRoom.memberLoginRequiredTitle")}</h1>
        <p className="text-sm text-muted-foreground mt-2">{t("dataRoom.memberLoginRequiredBody")}</p>
        <Button className="btn-brand rounded-xl mt-6" onClick={() => loginWithGoogle()}>
          {t("dataRoom.memberLoginCta")}
        </Button>
      </div>
    );
  }

  if (state.error === "not_found_or_forbidden") {
    return (
      <div className="mx-auto max-w-lg px-4 py-24 text-center" data-testid="member-data-room-forbidden">
        <LockKeyhole className="h-8 w-8 text-destructive mx-auto mb-4" />
        <h1 className="font-display text-2xl font-semibold">{t("dataRoom.memberNoAccessTitle")}</h1>
        <p className="text-sm text-muted-foreground mt-2">
          {t("dataRoom.memberNoAccessBodyGeneric", { defaultValue: "Your account is not on the invitation list for this room. Ask the admin to invite you." })}
        </p>
      </div>
    );
  }

  const { room, docs } = state;
  const isOwner = room.my_role === "owner";
  const canEdit = ["owner", "editor"].includes(room.my_role);

  return (
    <div className="overflow-x-clip" data-testid="member-data-room-page">
      <DashboardShell sections={sections} active="rooms" onSelect={goToTab}>
      <div className="space-y-6">
      <div>
        <Link to="/data-rooms" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary">
          <ArrowLeft className="h-3 w-3 rtl-flip" /> {t("dataRoom.backToMine")}
        </Link>
      </div>

      {/* Header */}
      <Card className="rounded-2xl border border-border p-6 sm:p-8">
        <div className="flex items-start gap-4">
          <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary flex-shrink-0">
            <Folder className="h-6 w-6" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
                {t("dataRoom.memberEyebrow")}
              </p>
              <Badge
                variant="outline"
                className={`text-[10px] rounded-full ${ROLE_TONES[room.my_role] || ROLE_TONES.viewer}`}
                data-testid="member-data-room-my-role"
              >
                {t(`dataRoom.role.${room.my_role || "viewer"}`, { defaultValue: room.my_role || "viewer" })}
              </Badge>
            </div>
            <RoomTitleEditor
              roomId={roomId}
              currentName={room.company_name}
              isOwner={isOwner}
              onRenamed={loadRoom}
            />
            {room.description && (
              <p className="text-sm text-muted-foreground mt-2">{room.description}</p>
            )}
            <p className="text-xs text-muted-foreground mt-3 inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-primary" /> {t("dataRoom.memberSecureNote")}
            </p>
          </div>
        </div>
      </Card>

      {/* Company card */}
      {room.company && (
        <Card className="rounded-2xl border border-border p-4 sm:p-6" data-testid="member-data-room-company">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary flex-shrink-0 overflow-hidden">
              {room.company.logo_url ? (
                <img src={room.company.logo_url} alt="" className="h-full w-full object-cover" />
              ) : (
                <Building2 className="h-5 w-5" />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
                {t("dataRoom.linkedCompany", { defaultValue: "Linked company" })}
              </p>
              <p className="font-display font-semibold" data-testid="member-data-room-company-name">
                {room.company.name}
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-muted-foreground">
                {room.company.industry && <span>{room.company.industry}</span>}
                {room.company.region && <span>· {room.company.region}</span>}
                {room.company.stage && <Badge variant="outline" className="rounded-full text-[10px] capitalize">{room.company.stage}</Badge>}
              </div>
              {room.company.website && (
                <a
                  href={room.company.website}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-primary hover:underline mt-1 inline-block"
                  data-testid="member-data-room-company-website"
                >
                  {room.company.website}
                </a>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Documents */}
      <Card className="rounded-2xl border border-border p-4 sm:p-6">
        <h2 className="font-display text-lg font-semibold mb-2">{t("dataRoom.documents")}</h2>
        {canEdit && (
          <div className="mb-3 rounded-xl border border-dashed border-border bg-muted/20 p-3 space-y-2" data-testid="member-data-room-uploader">
            <div className="flex flex-wrap items-center gap-2">
              <label className="inline-flex">
                <input
                  type="file"
                  className="hidden"
                  onChange={(e) => uploadDoc(e.target.files)}
                  disabled={uploading}
                  data-testid="member-data-room-upload-input"
                />
                <span className={`btn-brand inline-flex items-center gap-2 rounded-xl px-4 h-10 text-sm font-medium cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="member-data-room-upload-button">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {t("dataRoom.uploadCta", { defaultValue: "Upload document" })}
                </span>
              </label>
              <Input
                value={pendingTags}
                onChange={(e) => setPendingTags(e.target.value)}
                placeholder={t("dataRoom.tagsPlaceholder", { defaultValue: "Tags, comma separated (optional)" })}
                className="rounded-xl h-10 flex-1 min-w-[180px]"
                data-testid="member-data-room-upload-tags"
              />
            </div>
            <p className="text-[11px] text-muted-foreground">
              {t("dataRoom.uploadHint", { defaultValue: "PDF, Office, images and archives up to 50MB." })}
            </p>
          </div>
        )}
        {docs.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="member-data-room-empty">
            {t("dataRoom.memberEmpty")}
          </p>
        ) : (
          <div className="divide-y divide-border">
            {docs.map((d) => (
              <div key={d.doc_id} className="flex items-center gap-3 py-3" data-testid={`member-data-room-doc-${d.doc_id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{d.original_name}</p>
                  <p className="text-xs text-muted-foreground">{fmtSize(d.size_bytes)} · {fmtDate(d.uploaded_at)}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    {(d.tags || []).map((tg) => (
                      <Badge key={tg} variant="outline" className="rounded-full text-[10px]" data-testid={`member-data-room-tag-${d.doc_id}`}>
                        <Tag className="h-2.5 w-2.5 me-1" />{tg}
                      </Badge>
                    ))}
                    {canEdit && tagDraft.doc_id !== d.doc_id && (
                      <button
                        type="button"
                        className="text-[11px] text-primary hover:underline"
                        onClick={() => setTagDraft({ doc_id: d.doc_id, value: (d.tags || []).join(", ") })}
                        data-testid={`member-data-room-edit-tags-${d.doc_id}`}
                      >
                        {(d.tags || []).length ? t("dataRoom.editTags", { defaultValue: "Edit tags" }) : t("dataRoom.addTags", { defaultValue: "Add tags" })}
                      </button>
                    )}
                  </div>
                  {canEdit && tagDraft.doc_id === d.doc_id && (
                    <div className="mt-2 flex gap-2 flex-wrap">
                      <Input
                        value={tagDraft.value}
                        onChange={(e) => setTagDraft({ ...tagDraft, value: e.target.value })}
                        placeholder={t("dataRoom.tagsPlaceholder", { defaultValue: "Tags, comma separated (optional)" })}
                        className="rounded-lg h-9 flex-1 min-w-[160px]"
                        data-testid={`member-data-room-tags-input-${d.doc_id}`}
                      />
                      <Button size="sm" className="btn-brand rounded-lg h-9" onClick={() => saveTags(d.doc_id, tagDraft.value)} data-testid={`member-data-room-tags-save-${d.doc_id}`}>
                        {t("common.save")}
                      </Button>
                      <Button size="sm" variant="ghost" className="rounded-lg h-9" onClick={() => setTagDraft({ doc_id: null, value: "" })}>
                        {t("common.cancel")}
                      </Button>
                    </div>
                  )}
                </div>
                <Button
                  className="btn-brand rounded-xl gap-2"
                  size="sm"
                  onClick={() => download(d.doc_id, d.original_name)}
                  data-testid={`member-data-room-download-${d.doc_id}`}
                >
                  <Download className="h-4 w-4" /> {t("dataRoom.download")}
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Tasks */}
      <TasksPanel
        roomId={roomId}
        tasks={tasks}
        loading={tasksLoading}
        isOwner={isOwner}
        currentEmail={(user?.email || "").toLowerCase()}
        members={room.members || []}
        onChanged={loadTasks}
      />

      {/* Members */}
      <MembersPanel
        roomId={roomId}
        members={room.members || []}
        isOwner={isOwner}
        currentEmail={(user?.email || "").toLowerCase()}
        onChanged={loadRoom}
      />
      </div>
      </DashboardShell>
    </div>
  );
}

/**
 * Inline rename affordance shown in the room header. Only owners /
 * admins can edit; everyone else sees a static title. The `room_id`
 * (with its 6-hex-digit uniqueness suffix) is deliberately preserved
 * so renaming never breaks bookmarks or collides with another owner
 * that happens to pick the same name.
 */
function RoomTitleEditor({ roomId, currentName, isOwner, onRenamed }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(currentName || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(currentName || "");
  }, [currentName]);

  const save = async (e) => {
    e?.preventDefault?.();
    const next = value.trim();
    if (!next || next === currentName) {
      setEditing(false);
      setValue(currentName || "");
      return;
    }
    setSaving(true);
    try {
      await client.patch(`/data-rooms/${roomId}`, { company_name: next });
      toast.success(t("dataRoom.renamed", { defaultValue: "Data room renamed" }));
      setEditing(false);
      onRenamed();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  if (editing && isOwner) {
    return (
      <form onSubmit={save} className="mt-1 flex items-center gap-2 flex-wrap" data-testid="member-data-room-rename-form">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="rounded-xl h-11 text-lg font-display font-semibold flex-1 min-w-[220px]"
          autoFocus
          maxLength={160}
          data-testid="member-data-room-rename-input"
        />
        <Button
          type="submit"
          size="sm"
          className="btn-brand rounded-lg gap-1"
          disabled={saving || !value.trim()}
          data-testid="member-data-room-rename-save"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          {t("common.save")}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="rounded-lg gap-1"
          onClick={() => { setEditing(false); setValue(currentName || ""); }}
          disabled={saving}
          data-testid="member-data-room-rename-cancel"
        >
          <X className="h-3.5 w-3.5" />
          {t("common.cancel")}
        </Button>
      </form>
    );
  }

  return (
    <div className="mt-1 flex items-center gap-2 flex-wrap">
      <h1
        className="font-display text-2xl sm:text-3xl font-semibold"
        data-testid="member-data-room-title"
      >
        {currentName}
      </h1>
      {isOwner && (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground hover:text-primary hover:border-primary transition-colors"
          aria-label={t("dataRoom.rename", { defaultValue: "Rename" })}
          data-testid="member-data-room-rename-btn"
        >
          <Pencil className="h-3 w-3" />
          {t("dataRoom.rename", { defaultValue: "Rename" })}
        </button>
      )}
    </div>
  );
}

/**
 * Room tasks list. All members can read. Owner + admin can add / delete
 * / reassign / edit. The assignee (plus owner + admin) can toggle status.
 */
function TasksPanel({ roomId, tasks, loading, isOwner, currentEmail, members, onChanged }) {
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [assignee, setAssignee] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [priority, setPriority] = useState("medium");
  const [saving, setSaving] = useState(false);

  const assignableMembers = members.map((m) => ({
    email: m.email,
    label: m.name || t("dataRoom.memberPlaceholder", { defaultValue: "Invited member" }),
  }));

  const resetForm = () => {
    setCreating(false);
    setTitle("");
    setAssignee("");
    setDueAt("");
    setPriority("medium");
  };

  const create = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      await client.post(`/data-rooms/${roomId}/tasks`, {
        title: title.trim(),
        assignee_email: assignee || currentEmail,
        due_at: dueAt,
        priority,
      });
      toast.success(t("dataRoom.taskCreated", { defaultValue: "Task added" }));
      resetForm();
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (task) => {
    const next = task.status === "done" ? "open" : "done";
    try {
      await client.patch(`/data-rooms/${roomId}/tasks/${task.task_id}`, { status: next });
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    }
  };

  const remove = async (task) => {
    if (!window.confirm(t("dataRoom.taskConfirmDelete", { defaultValue: "Delete this task?" }))) return;
    try {
      await client.delete(`/data-rooms/${roomId}/tasks/${task.task_id}`);
      toast.success(t("dataRoom.taskDeleted", { defaultValue: "Task deleted" }));
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    }
  };

  return (
    <Card className="rounded-2xl border border-border p-4 sm:p-6" data-testid="member-data-room-tasks">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h2 className="font-display text-lg font-semibold inline-flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-primary" />
          {t("dataRoom.tasksTitle", { defaultValue: "Tasks" })}
          <span className="text-xs text-muted-foreground font-normal">({tasks.length})</span>
        </h2>
        {isOwner && !creating && (
          <Button
            size="sm"
            className="btn-brand rounded-xl gap-1"
            onClick={() => setCreating(true)}
            data-testid="member-data-room-task-new"
          >
            <Plus className="h-3.5 w-3.5" /> {t("dataRoom.taskNew", { defaultValue: "New task" })}
          </Button>
        )}
      </div>

      {isOwner && creating && (
        <form onSubmit={create} className="rounded-xl border border-border p-3 mb-3 space-y-2 bg-muted/20" data-testid="member-data-room-task-form">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("dataRoom.taskTitlePlaceholder", { defaultValue: "What needs to happen?" })}
            className="rounded-lg h-9"
            required
            data-testid="member-data-room-task-title-input"
          />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <select
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
              className="rounded-lg h-9 border border-input bg-transparent px-2 text-sm"
              data-testid="member-data-room-task-assignee-input"
            >
              <option value="">
                {t("dataRoom.taskAssignToMe", { defaultValue: "Assign to me" })}
              </option>
              {assignableMembers.map((m) => (
                <option key={m.email} value={m.email}>{m.label}</option>
              ))}
            </select>
            <Input
              type="date"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              className="rounded-lg h-9"
              data-testid="member-data-room-task-due-input"
            />
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="rounded-lg h-9 border border-input bg-transparent px-2 text-sm"
              data-testid="member-data-room-task-priority-input"
            >
              <option value="low">{t("dataRoom.priorityLow", { defaultValue: "Low" })}</option>
              <option value="medium">{t("dataRoom.priorityMedium", { defaultValue: "Medium" })}</option>
              <option value="high">{t("dataRoom.priorityHigh", { defaultValue: "High" })}</option>
            </select>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" className="rounded-lg" onClick={resetForm}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" size="sm" className="btn-brand rounded-lg gap-1" disabled={saving} data-testid="member-data-room-task-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              {t("common.save")}
            </Button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin text-primary inline" /></div>
      ) : tasks.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center" data-testid="member-data-room-tasks-empty">
          {t("dataRoom.tasksEmpty", { defaultValue: "No tasks in this room yet." })}
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {tasks.map((task) => {
            const isAssignee = (task.assignee_email || "").toLowerCase() === currentEmail;
            const canToggle = isOwner || isAssignee;
            const done = task.status === "done";
            const assigneeLabel = task.assignee_name
              || (task.assignee_email ? t("dataRoom.taskPendingUser", { defaultValue: "Pending user" }) : t("dataRoom.taskUnassigned", { defaultValue: "Unassigned" }));
            return (
              <li key={task.task_id} className="flex items-start gap-3 py-3" data-testid={`member-data-room-task-${task.task_id}`}>
                <button
                  type="button"
                  onClick={() => canToggle && toggleStatus(task)}
                  disabled={!canToggle}
                  aria-label={done ? "Reopen task" : "Mark done"}
                  className={`mt-0.5 shrink-0 ${canToggle ? "cursor-pointer text-primary" : "cursor-not-allowed text-muted-foreground"}`}
                  data-testid={`member-data-room-task-toggle-${task.task_id}`}
                >
                  {done ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5" />}
                </button>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium ${done ? "line-through text-muted-foreground" : ""}`}>{task.title}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Users2 className="h-3 w-3" />
                      {assigneeLabel}
                    </span>
                    {task.due_at && (
                      <span>· {t("dataRoom.taskDue", { defaultValue: "Due" })} {fmtDueDate(task.due_at)}</span>
                    )}
                    <Badge variant="outline" className={`rounded-full text-[10px] ${PRIORITY_TONES[task.priority] || PRIORITY_TONES.medium}`}>
                      {t(`dataRoom.priority${task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}`, { defaultValue: task.priority })}
                    </Badge>
                  </div>
                </div>
                {isOwner && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-destructive/70 hover:text-destructive"
                    onClick={() => remove(task)}
                    aria-label="Delete task"
                    data-testid={`member-data-room-task-delete-${task.task_id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

/**
 * Invited members list. Everyone can see; owner + admin can invite,
 * change role, or remove.
 */
function MembersPanel({ roomId, members, isOwner, currentEmail, onChanged }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");
  const [saving, setSaving] = useState(false);

  const invite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSaving(true);
    try {
      const res = await client.post(`/data-rooms/${roomId}/members`, {
        email: email.trim(),
        role,
      });
      toast.success(res.data?.registered
        ? t("dataRoom.invited", { defaultValue: "Access granted" })
        : t("dataRoom.invitedUnregistered", { defaultValue: "Access granted — they must sign in with this email to open the room." }));
      setEmail("");
      setRole("viewer");
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (m, newRole) => {
    try {
      await client.post(`/data-rooms/${roomId}/members`, { email: m.email, role: newRole });
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    }
  };

  const remove = async (m) => {
    if (!window.confirm(t("dataRoom.confirmRevoke", { defaultValue: "Remove this member?" }))) return;
    try {
      await client.delete(`/data-rooms/${roomId}/members/${encodeURIComponent(m.email)}`);
      toast.success(t("dataRoom.accessRevoked", { defaultValue: "Access revoked" }));
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    }
  };

  return (
    <Card className="rounded-2xl border border-border p-4 sm:p-6" data-testid="member-data-room-members">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg font-semibold inline-flex items-center gap-2">
          <Users2 className="h-4 w-4 text-primary" />
          {t("dataRoom.accessTitle")}
          <span className="text-xs text-muted-foreground font-normal">({members.length})</span>
        </h2>
      </div>

      {isOwner && (
        <form onSubmit={invite} className="rounded-xl border border-border p-3 mb-3 space-y-2 bg-muted/20" data-testid="member-data-room-invite-form">
          <p className="text-xs text-muted-foreground">{t("dataRoom.accessHint")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px_auto] gap-2">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("dataRoom.inviteEmailLabel")}
              className="rounded-lg h-9"
              required
              data-testid="member-data-room-invite-email"
            />
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="rounded-lg h-9 border border-input bg-transparent px-2 text-sm"
              data-testid="member-data-room-invite-role"
            >
              <option value="viewer">{t("dataRoom.role.viewer", { defaultValue: "Viewer" })}</option>
              <option value="editor">{t("dataRoom.role.editor", { defaultValue: "Editor" })}</option>
              <option value="owner">{t("dataRoom.role.owner", { defaultValue: "Owner" })}</option>
            </select>
            <Button
              type="submit"
              size="sm"
              className="btn-brand rounded-lg gap-1"
              disabled={saving}
              data-testid="member-data-room-invite-submit"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
              {t("dataRoom.invite")}
            </Button>
          </div>
        </form>
      )}

      {members.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center" data-testid="member-data-room-members-empty">
          {t("dataRoom.accessEmpty")}
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {members.map((m) => {
            const isMe = m.email === currentEmail;
            const memberKey = m.member_id || m.email;
            const displayName = isMe
              ? t("dataRoom.you", { defaultValue: "you" })
              : (m.name || t("dataRoom.memberPlaceholder", { defaultValue: "Invited member" }));
            return (
              <li
                key={memberKey}
                className="flex items-center gap-3 py-2.5"
                data-testid={`member-data-room-member-${memberKey}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">
                    {displayName}
                    {isMe && <span className="ms-1 text-[10px] text-muted-foreground">({t("dataRoom.roleYou", { defaultValue: "you" })})</span>}
                  </p>
                </div>
                {isOwner && !isMe ? (
                  <select
                    value={m.role}
                    onChange={(e) => changeRole(m, e.target.value)}
                    className="rounded-lg h-8 border border-input bg-transparent px-2 text-xs"
                    data-testid={`member-data-room-member-role-${memberKey}`}
                  >
                    <option value="viewer">{t("dataRoom.role.viewer", { defaultValue: "Viewer" })}</option>
                    <option value="editor">{t("dataRoom.role.editor", { defaultValue: "Editor" })}</option>
                    <option value="owner">{t("dataRoom.role.owner", { defaultValue: "Owner" })}</option>
                  </select>
                ) : (
                  <Badge
                    variant="outline"
                    className={`rounded-full text-[10px] ${ROLE_TONES[m.role] || ROLE_TONES.viewer}`}
                    data-testid={`member-data-room-member-badge-${memberKey}`}
                  >
                    {t(`dataRoom.role.${m.role}`, { defaultValue: m.role })}
                  </Badge>
                )}
                {isOwner && !isMe && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive/70 hover:text-destructive"
                    onClick={() => remove(m)}
                    aria-label="Remove member"
                    data-testid={`member-data-room-member-remove-${memberKey}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
